import React, { useState, useEffect } from 'react';
import { useFeatureLimits } from './FeatureLimitsProvider';
import { Modal, Button, Alert, Card } from 'react-bootstrap';
import { FaLock, FaCrown, FaExclamationTriangle } from 'react-icons/fa';
import { FEATURE_NAMES, FEATURE_DISPLAY_NAMES } from '../../apis/featureLimits';
import { useNavigate } from 'react-router-dom';

const PageDisableOverlay = ({ 
  featureName, 
  children, 
  showUpgradeButton = true,
  customMessage = null 
}) => {
  const navigate = useNavigate();
  const {
    hasReachedLimit,
    getFeatureLimit,
    isFreeUser,
    isSubscriber,
    getSubscriptionDisplayName
  } = useFeatureLimits();

  const [isDisabled, setIsDisabled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);

  useEffect(() => {
    checkLimitStatus();
  }, [featureName]);

  const checkLimitStatus = () => {
    const limit = getFeatureLimit(featureName);
    const isBlocked = limit && limit.limit_value === 0;
    const isLimitReached = hasReachedLimit(featureName);
    
    if (isBlocked || isLimitReached) {
      setIsDisabled(true);
      setLimitInfo({
        isBlocked,
        isLimitReached,
        limit,
        featureName
      });
    } else {
      setIsDisabled(false);
      setLimitInfo(null);
    }
  };

  const getFeatureDisplayName = () => {
    return FEATURE_DISPLAY_NAMES[featureName] || featureName.replace('_', ' ').toUpperCase();
  };

  const getErrorMessage = () => {
    if (customMessage) return customMessage;

    if (limitInfo?.isBlocked) {
      return `${getFeatureDisplayName()} is not available for free users. Please upgrade to access this feature.`;
    }

    if (limitInfo?.isLimitReached) {
      const resetDate = limitInfo.limit?.resetDate ? new Date(limitInfo.limit.resetDate).toLocaleDateString() : 'soon';
      return `You've reached your limit for ${getFeatureDisplayName()}. Your limit will reset ${resetDate}.`;
    }

    return `You don't have access to ${getFeatureDisplayName()}.`;
  };

  const getIcon = () => {
    if (limitInfo?.isBlocked) return <FaLock className="text-danger" style={{ fontSize: '3rem' }} />;
    if (limitInfo?.isLimitReached) return <FaExclamationTriangle className="text-warning" style={{ fontSize: '3rem' }} />;
    return <FaLock className="text-danger" style={{ fontSize: '3rem' }} />;
  };

  const getTitle = () => {
    if (limitInfo?.isBlocked) return 'Feature Not Available';
    if (limitInfo?.isLimitReached) return 'Limit Reached';
    return 'Access Denied';
  };

  const handleUpgrade = () => {
    navigate('/subscription');
  };

  const handleClose = () => {
    setShowModal(false);
    // Redirect to home when closing the view limits popup
    navigate('/home');
  };

  // Show modal when limit is reached
  useEffect(() => {
    if (isDisabled) {
      setShowModal(true);
    }
  }, [isDisabled]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Disabled overlay */}
      {isDisabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}
        >
          <Card className="text-center p-4" style={{ maxWidth: '400px', pointerEvents: 'auto' }}>
            <Card.Body>
              {getIcon()}
              <h5 className="mt-3 mb-3">{getTitle()}</h5>
              <p className="text-muted mb-4">{getErrorMessage()}</p>
              
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-2">
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={() => navigate('/feature-limits')}
                    className="flex-fill"
                  >
                    View Limits
                  </Button>
                  
                  {showUpgradeButton && (
                    <Button 
                      variant="success" 
                      size="lg"
                      onClick={handleUpgrade}
                      className="flex-fill"
                    >
                      <FaCrown className="me-2" />
                      Upgrade
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline-danger" 
                    size="lg"
                    onClick={() => navigate('/home')}
                    className="flex-fill"
                  >
                    Go Home
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Disabled content */}
      <div style={{ 
        opacity: isDisabled ? 0.3 : 1, 
        pointerEvents: isDisabled ? 'none' : 'auto',
        filter: isDisabled ? 'blur(2px)' : 'none'
      }}>
        {children}
      </div>

      {/* Modal for better UX */}
      <Modal show={showModal} onHide={handleClose} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{getTitle()}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {getIcon()}
          <p className="mt-3">{getErrorMessage()}</p>
          
          {limitInfo?.isLimitReached && !limitInfo?.isBlocked && (
            <Alert variant="info" className="mt-3">
              <strong>Remaining:</strong> {limitInfo.limit ? limitInfo.limit.remaining_limit : 0} uses available
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <div className="d-flex gap-2 w-100">
            <Button 
              variant="primary" 
              onClick={() => navigate('/feature-limits')}
              className="flex-fill"
            >
              View Limits
            </Button>
            
            {showUpgradeButton && (
              <Button 
                variant="success" 
                onClick={handleUpgrade}
                className="flex-fill"
              >
                <FaCrown className="me-2" />
                Upgrade
              </Button>
            )}
            
            <Button variant="outline-danger" onClick={handleClose} className="flex-fill">
              Go Home
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PageDisableOverlay;
